import React, { useEffect, useState } from 'react';
import { Heart, Share2, ChevronRight, ShoppingCart, Minus, Plus, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigation } from '../context/NavigationContext';
import { useApp, MOCK_PRODUCTS } from '../context/AppContext';
import { Screen } from '../types';
import { Header } from '../components/ui/Header';
import { Button } from '../components/ui/Button';
import { Rating } from '../components/ui/Rating';
import { Badge } from '../components/ui/Badge';
export function ProductDetailScreen() {
  const {
    currentParams,
    navigate
  } = useNavigation();
  const {
    addToCart,
    wishlist,
    toggleWishlist,
    cart
  } = useApp();
  const productId = currentParams?.productId;
  const product = MOCK_PRODUCTS.find(p => p.id === productId) || MOCK_PRODUCTS[0];
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState(product.variants?.colors?.[0]);
  const [selectedSize, setSelectedSize] = useState(product.variants?.sizes?.[0]);
  const [quantity, setQuantity] = useState(1);
  const [showToast, setShowToast] = useState(false);
  const isWishlisted = wishlist.includes(product.id);
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => setShowToast(false), 2200);
      return () => clearTimeout(timer);
    }
  }, [showToast]);
  const handleAddToCart = () => {
    addToCart({
      product,
      quantity,
      selectedColor,
      selectedSize
    });
    setShowToast(true);
  };
  const handleBuyNow = () => {
    handleAddToCart();
    navigate(Screen.CART);
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} className="min-h-screen bg-gray-50 pb-24" data-id="element-1062">
      <Header transparent rightElement={<div className="flex gap-2" data-id="element-1064">
            <button className="p-2 bg-white/80 backdrop-blur-sm rounded-full shadow-sm" data-id="element-1065">
              <Share2 className="w-5 h-5 text-gray-800" data-id="element-1066" />
            </button>
            <button onClick={() => navigate(Screen.CART)} className="p-2 bg-white/80 backdrop-blur-sm rounded-full shadow-sm" data-id="element-1067">
              <Badge count={cart.length} data-id="element-1068">
                <ShoppingCart className="w-5 h-5 text-gray-800" data-id="element-1069" />
              </Badge>
            </button>
          </div>} data-id="element-1063" />

      {/* Image Gallery */}
      <div className="bg-white -mt-[60px] pt-[60px]" data-id="element-1070">
        <div className="aspect-square w-full relative" data-id="element-1071">
          <img src={product.images[selectedImage]} alt={product.name} className="w-full h-full object-cover" data-id="element-1072" />
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2" data-id="element-1073">
            {product.images.map((_, idx) => <button key={idx} onClick={() => setSelectedImage(idx)} className={`w-2 h-2 rounded-full transition-all ${selectedImage === idx ? 'bg-primary w-4' : 'bg-gray-300'}`} data-id="element-1074" />)}
          </div>
        </div>
      </div>

      {/* Product Info */}
      <div className="bg-white p-4 mb-2 shadow-sm" data-id="element-1075">
        <div className="flex justify-between items-start gap-4 mb-2" data-id="element-1076">
          <h1 className="text-xl font-bold text-gray-900 leading-tight" data-id="element-1077">
            {product.name}
          </h1>
          <button onClick={() => toggleWishlist(product.id)} className="p-2 bg-gray-50 rounded-full shrink-0" data-id="element-1078">
            <Heart className={`w-6 h-6 ${isWishlisted ? 'fill-error text-error' : 'text-gray-400'}`} data-id="element-1079" />
          </button>
        </div>

        <div className="flex items-center gap-3 mb-4" data-id="element-1080">
          <Rating value={product.rating} count={product.reviews} data-id="element-1081" />
          <span className="text-sm text-gray-400" data-id="element-1082">|</span>
          <span className="text-sm text-success font-medium" data-id="element-1083">In Stock</span>
        </div>

        <div className="flex items-end gap-2" data-id="element-1084">
          <span className="text-3xl font-bold text-primary" data-id="element-1085">
            ₦{product.price.toLocaleString()}
          </span>
          {product.originalPrice && <span className="text-lg text-gray-400 line-through mb-1" data-id="element-1086">
              ₦{product.originalPrice.toLocaleString()}
            </span>}
          {product.discount && <span className="bg-error/10 text-error text-xs font-bold px-2 py-1 rounded mb-1 ml-2" data-id="element-1087">
              -{product.discount}%
            </span>}
        </div>
      </div>

      {/* Variants & Quantity */}
      <div className="bg-white p-4 mb-2 shadow-sm space-y-5" data-id="element-1088">
        {product.variants?.colors && <div data-id="element-1089">
            <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1090">Color</h3>
            <div className="flex gap-3" data-id="element-1091">
              {product.variants.colors.map(color => <button key={color} onClick={() => setSelectedColor(color)} className={`w-10 h-10 rounded-full border-2 transition-all ${selectedColor === color ? 'border-primary scale-110' : 'border-transparent shadow-sm'}`} style={{
            backgroundColor: color
          }} data-id="element-1092" />)}
            </div>
          </div>}

        {product.variants?.sizes && <div data-id="element-1093">
            <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1094">Size</h3>
            <div className="flex gap-3 flex-wrap" data-id="element-1095">
              {product.variants.sizes.map(size => <button key={size} onClick={() => setSelectedSize(size)} className={`px-4 py-2 rounded-xl border text-sm font-medium transition-all ${selectedSize === size ? 'border-primary bg-primary/5 text-primary' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-1096">
                  {size}
                </button>)}
            </div>
          </div>}

        <div data-id="element-1097">
          <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-1098">Quantity</h3>
          <div className="flex items-center gap-4" data-id="element-1099">
            <div className="flex items-center bg-gray-50 rounded-xl border border-gray-100 p-1" data-id="element-1100">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-white rounded-lg transition-colors" data-id="element-1101">
                <Minus className="w-4 h-4" data-id="element-1102" />
              </button>
              <span className="w-12 text-center font-medium text-gray-900" data-id="element-1103">
                {quantity}
              </span>
              <button onClick={() => setQuantity(quantity + 1)} className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-white rounded-lg transition-colors" data-id="element-1104">
                <Plus className="w-4 h-4" data-id="element-1105" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Seller Info */}
      <div className="bg-white p-4 mb-2 shadow-sm flex items-center justify-between" data-id="element-1106">
        <div className="flex items-center gap-3" data-id="element-1107">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center" data-id="element-1108">
            <span className="text-xl" data-id="element-1109">🏪</span>
          </div>
          <div data-id="element-1110">
            <h4 className="font-medium text-gray-900" data-id="element-1111">{product.seller.name}</h4>
            <div className="flex items-center gap-1 text-sm text-gray-500" data-id="element-1112">
              <Rating value={product.seller.rating} data-id="element-1113" />
            </div>
          </div>
        </div>
        <button onClick={() => navigate(Screen.VENDOR_STORE, {
        sellerName: product.seller.name
      })} className="text-sm font-medium text-primary px-3 py-1.5 border border-primary rounded-lg active:scale-[0.98] transition-transform" data-id="element-1114">
          Visit Store
        </button>
      </div>

      {/* Description */}
      <div className="bg-white p-4 shadow-sm mb-4" data-id="element-1115">
        <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1116">Description</h3>
        <p className="text-gray-600 text-sm leading-relaxed" data-id="element-1117">
          {product.description}
        </p>
      </div>

      {/* Added to Cart Toast */}
      <AnimatePresence data-id="element-1118">
        {showToast && <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 20
      }} transition={{
        type: 'spring',
        stiffness: 400,
        damping: 25
      }} className="fixed bottom-24 left-4 right-4 z-[60] flex justify-center pointer-events-none" data-id="element-1119">
            <div className="bg-gray-900 text-white px-5 py-3 rounded-2xl shadow-xl flex items-center gap-3 pointer-events-auto" data-id="element-1120">
              <CheckCircle2 className="w-5 h-5 text-success shrink-0" data-id="element-1121" />
              <span className="text-sm font-medium" data-id="element-1122">Added to cart</span>
              <button onClick={() => {
            setShowToast(false);
            navigate(Screen.CART);
          }} className="text-sm font-bold text-accent ml-2 hover:underline" data-id="element-1123">
                View Cart
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 pb-safe z-50" data-id="element-1124">
        <div className="max-w-md mx-auto flex gap-3" data-id="element-1125">
          <Button variant="outline" className="flex-1" onClick={handleAddToCart} data-id="element-1126">
            Add to Cart
          </Button>
          <Button className="flex-1" onClick={handleBuyNow} data-id="element-1127">
            Buy Now
          </Button>
        </div>
      </div>
    </motion.div>;
}